import React, { useEffect, useState } from 'react';
import { fetchDonationsByEmail, createDonation } from '../services/donationService';
import { fetchConfig } from '../services/configService';
import { motion } from 'framer-motion';
import { T, Toast, getStatusClass, Th, Tr } from './theme';
import { useAuth } from './AuthContext';

const FALLBACK_TYPES = ['General', 'Event', 'Temple Maintenance', 'Education', 'Other'];
const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } };
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

const DonatePage = () => {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [form, setForm] = useState({ donationAmount: '', donationType: 'General' });
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [donationTypes, setDonationTypes] = useState(FALLBACK_TYPES);
  const { role } = useAuth();
  const isAdmin = (role || '').split(',').map(s => s.trim().toUpperCase()).includes('ADMIN');
  const userEmail = localStorage.getItem('vrvEmail') || '';

  const load = async () => {
    setLoading(true);
    const data = await fetchDonationsByEmail(userEmail);
    data.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    setDonations(data);
    setLoading(false);
  };

  useEffect(() => {
    if (userEmail) load();
    fetchConfig('DONATION_TYPE').then(types => {
      if (types.length) { setDonationTypes(types); setForm(p => ({ ...p, donationType: types[0] })); }
    });
  }, [userEmail]);

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.donationAmount || Number(form.donationAmount) <= 0) { showMsg('Please enter a valid amount', 'error'); return; }
    setSubmitting(true);
    const now = new Date().toISOString();
    const reviewerFullName = `${localStorage.getItem('vrvName') || ''} ${localStorage.getItem('vrvSurname') || ''}`.trim() || userEmail;
    const donationPayload = isAdmin
      ? { emailID: userEmail, donationAmount: form.donationAmount, donationType: form.donationType, createdAt: now, status: 'APPROVED', recordedBy: userEmail, reviewedBy: userEmail, reviewerFullName, reviewedAt: now, reviewComments: 'Auto-approved by admin' }
      : { emailID: userEmail, donationAmount: form.donationAmount, donationType: form.donationType, createdAt: now };
    const res = await createDonation(donationPayload);
    if (res.statusCode === 201) {
      showMsg(isAdmin ? 'Donation recorded & approved!' : 'Donation submitted for approval!', 'success');
      setForm({ donationAmount: '', donationType: donationTypes[0] || 'General' });
      load();
    } else showMsg(res.message || 'Failed', 'error');
    setSubmitting(false);
  };

  const approved = donations.filter(d => d.status === 'APPROVED');
  const totalApproved = approved.reduce((s, d) => s + Number(d.donationAmount || 0), 0);
  const filtered = donations.filter(d =>
    !search || Object.values(d).some(v => String(v).toLowerCase().includes(search.toLowerCase()))
  );

  const fmt = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div style={{ background: T.bg, color: T.ink, minHeight: '100vh' }} className="">
      <section className="vrv-hero">
        <div className="vrv-container">
          <div className="relative z-10">
            <p className="vrv-tag mb-3">Support the Mission</p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>Donations</h1>
            <p className="text-base mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>Support temple activities, community welfare, and educational initiatives.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl">
              {[
                { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, value: `₹${totalApproved.toLocaleString()}`, label: 'Total Donated' },
                { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, value: approved.length, label: 'Approved' },
                { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, value: donations.filter(d => d.status === 'PENDING').length, label: 'Pending' },
                { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, value: donations.filter(d => d.status === 'REJECTED').length, label: 'Rejected' },
              ].map((k, i) => (
                <div key={i} className="vrv-kpi-card">
                  <div className="mb-1.5" style={{ color: 'rgba(255,255,255,0.8)' }}>{k.icon}</div>
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{k.value}</p>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>{k.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="vrv-container py-10">

        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: 0.1 }}
          className="vrv-card mb-8">
          <div className="px-8 py-5" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', background: 'linear-gradient(135deg, #FDFBF7, white)' }}>
            <p className="vrv-tag mb-1">Make a Contribution</p>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: T.ink }}>Submit a Donation</h2>
          </div>
          <div className="px-8 py-6">
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="vrv-label">Donation Type</label>
                <select value={form.donationType} onChange={e => setForm({ ...form, donationType: e.target.value })} className="vrv-select">
                  {donationTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="vrv-label">Amount (₹)</label>
                <input type="number" min="1" value={form.donationAmount} onChange={e => setForm({ ...form, donationAmount: e.target.value })}
                  placeholder="Enter amount" className="vrv-input" required />
              </div>
              <button type="submit" disabled={submitting} className="btn-gold" style={{ height: '48px' }}>
                {submitting ? 'Submitting…' : 'Submit Donation →'}
              </button>
            </form>
          </div>
        </motion.div>

        {/* ── History ─────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: 0.2 }}
          className="vrv-card overflow-hidden" style={{ transform: 'none' }}>
          <div className="px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', background: 'linear-gradient(135deg, #FDFBF7, white)' }}>
            <div>
              <p className="vrv-tag mb-1">History</p>
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: T.ink }}>My Donations</h3>
            </div>
            <div className="relative w-full sm:w-52">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: T.muted }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} className="vrv-input pl-10" style={{ minHeight: '40px' }} />
            </div>
          </div>

          {loading && (
            <div className="text-center py-16"><p className="text-sm" style={{ color: T.muted }}>Loading donations…</p></div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-16">
              <div className="text-4xl mb-3">💛</div>
              <p className="font-semibold" style={{ color: T.muted }}>No donations found</p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <>
              {/* Desktop Table */}
              <div className="desktop-table overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      {['Donation ID', 'Type', 'Amount', 'Date', 'Status', 'Approved By'].map(h => (
                        <Th key={h}>{h}</Th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((d, i) => (
                      <Tr key={d.donationID || i}>
                        <td className="px-4 py-3.5">
                          <span className="font-mono text-[11px]" style={{ color: T.muted }} title={d.donationID || ''}>
                            {d.donationID || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 font-medium" style={{ color: T.ink }}>{d.donationType}</td>
                        <td className="px-4 py-3.5 font-bold" style={{ color: T.navy }}>₹{Number(d.donationAmount).toLocaleString()}</td>
                        <td className="px-4 py-3.5 whitespace-nowrap text-xs" style={{ color: T.muted }}>{fmt(d.createdAt)}</td>
                        <td className="px-4 py-3.5"><span className={getStatusClass(d.status)}>{d.status}</span></td>
                        <td className="px-4 py-3.5 text-xs" style={{ color: T.muted }}>
                          {d.status === 'APPROVED' ? (d.reviewerFullName || d.reviewedBy || '—') : '—'}
                        </td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards p-4 space-y-3">
                {filtered.map((d, i) => (
                  <div key={d.donationID || i} className="vrv-card p-4" style={{ boxShadow: 'none', border: '1.5px solid rgba(0,0,0,0.07)' }}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>₹{Number(d.donationAmount).toLocaleString()}</span>
                      <span className={getStatusClass(d.status)}>{d.status}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span style={{ color: T.muted }}>Type: </span><span style={{ color: T.ink }}>{d.donationType || '—'}</span></div>
                      <div><span style={{ color: T.muted }}>Date: </span><span style={{ color: T.ink }}>{fmt(d.createdAt)}</span></div>
                      <div><span style={{ color: T.muted }}>Approved By: </span><span style={{ color: T.ink }}>{d.status === 'APPROVED' ? (d.reviewerFullName || d.reviewedBy || '—') : '—'}</span></div>
                      <div className="col-span-2"><span style={{ color: T.muted }}>ID: </span><span className="font-mono text-[10px]" style={{ color: T.muted }}>{d.donationID || '—'}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </motion.div>
      </div>

      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default DonatePage;
